import React, { useRef, useState } from 'react'
import { AlertCircle, CheckCircle2, Upload } from 'lucide-react'
import {
  DcButton,
  DcDiv,
  DcFlexBox,
  DcInput,
  SectionContent,
  PixelFactoryContext,
  colors,
} from 'pixel-base'
import { post } from 'utils/api-routes.util'
import * as XLSX from 'xlsx'
import { IPixelFactory } from '../../../../decl'

interface OrderStatus {
  orderId: string
  status: string
  isInvalid?: boolean
}

interface OrderStatusUpdateProps {
  handelClose: () => void
  setReload: (_value: boolean) => void
  columns: number
}

interface OrderValidationResponse {
  messgae?: string
  invalid_orders?: string[]
  error?: string
}

interface ApiResponse {
  response?: OrderValidationResponse
}

export default function OrderStatusUpdate({
  setReload,
  handelClose,
  columns,
}: OrderStatusUpdateProps) {
  const { hidePageLoader, showPageLoader, showSuccess } =
    React.useContext<IPixelFactory>(PixelFactoryContext)

  const [orders, setOrders] = useState<OrderStatus[]>([])
  const [jsonData, setjsonData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [invalidOrderIds, setInvalidOrderIds] = useState<number[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    showPageLoader()

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as Record<
          string,
          any
        >[]

        if (jsonData.length === 0) {
          setErrorMessage('The uploaded file is empty.')
          hidePageLoader()
          return
        }

        const firstRow = jsonData[0]
        const hasOrderIdColumn =
          'orderId' in firstRow ||
          'OrderId' in firstRow ||
          'orderid' in firstRow ||
          'ORDERID' in firstRow
        const hasStatusColumn =
          'status' in firstRow || 'Status' in firstRow || 'STATUS' in firstRow

        if (!hasOrderIdColumn || !hasStatusColumn) {
          setErrorMessage(
            'The file must contain "orderId" and "status" columns.',
          )
          hidePageLoader()
          return
        }
        setjsonData(jsonData)

        const normalizedData = jsonData.map((row: Record<string, any>) => {
          const orderIdKey =
            Object.keys(row).find(
              (key) =>
                key.toLowerCase() === 'orderid' ||
                key.toLowerCase() === 'order_id' ||
                key.toLowerCase() === 'order id',
            ) || ''

          const statusKey =
            Object.keys(row).find((key) => key.toLowerCase() === 'status') || ''

          return {
            orderId: String(row[orderIdKey]),
            status: String(row[statusKey]),
          }
        })

        // Check for duplicate order IDs
        const orderIds = normalizedData.map((order) => order.orderId)
        const uniqueOrderIds = new Set(orderIds)
        if (orderIds.length !== uniqueOrderIds.size) {
          // Find duplicate order IDs
          const duplicateIds = orderIds.filter(
            (id, index) => orderIds.indexOf(id) !== index,
          )

          // Mark orders with duplicate IDs
          const markedData = normalizedData.map((order) => ({
            ...order,
            isInvalid: duplicateIds.includes(order.orderId),
          }))

          setOrders(markedData)
          setErrorMessage(
            'Duplicate order IDs found in the file. Please ensure each order ID is unique.',
          )
          hidePageLoader()
          return
        }

        setOrders(normalizedData)
        setErrorMessage(null)
        hidePageLoader()

        // Validate new orders
        setTimeout(() => validateOrderIds(normalizedData), 100)
      } catch (error) {
        setErrorMessage(
          "Failed to parse the Excel file. Please ensure it's a valid Excel file.",
        )
        hidePageLoader()
      }
    }

    reader.onerror = () => {
      setErrorMessage('Error reading the file.')
      hidePageLoader()
    }

    reader.readAsArrayBuffer(file)
  }

  const handleUpdateStatuses = async () => {
    if (orders.length === 0) return

    setIsLoading(true)
    showPageLoader()
    setErrorMessage(null)
    setSuccessMessage(null)

    const updateData = jsonData.reduce(
      (acc, row) => ({
        ...acc,
        [String(row.orderId)]: String(row.status),
      }),
      {},
    )

    post('/orders/bulk/status', updateData).subscribe({
      next: () => {
        hidePageLoader()
        setIsLoading(false)
        showSuccess('Order statuses updated successfully.')
        handelClose()
        setReload(true)
      },
      error: (error) => {
        hidePageLoader()
        setIsLoading(false)
        setErrorMessage(error.message || 'Failed to update order statuses.')
      },
    })
  }

  const validateOrderIds = (orderList: OrderStatus[] = orders) => {
    if (orderList.length === 0) return

    setErrorMessage(null)

    const updateData = orderList.map((order) => order.orderId)

    post('/orders/verify-order-ids', { order_ids: updateData }).subscribe(
      (res: any) => {
        const response = res as ApiResponse
        const responseData =
          response.response || (res as OrderValidationResponse)

        if (responseData.messgae === 'success') {
          setInvalidOrderIds([])
          setSuccessMessage('All order IDs are valid.')
          const updatedOrders = orderList.map((order) => ({
            ...order,
            isInvalid: false,
          }))
          setOrders(updatedOrders)
        } else if (responseData.invalid_orders) {
          setInvalidOrderIds(
            responseData.invalid_orders.map((id) => parseInt(id)),
          )
          setErrorMessage(
            `${responseData.invalid_orders.length} invalid order IDs found.`,
          )

          const updatedOrders = orderList.map((order) => ({
            ...order,
            isInvalid: responseData.invalid_orders?.includes(order.orderId),
          }))
          setOrders(updatedOrders)
        } else if (responseData.error) {
          setErrorMessage(responseData.error)

          if (responseData.invalid_orders) {
            const updatedOrders = orderList.map((order) => ({
              ...order,
              isInvalid: responseData.invalid_orders?.includes(order.orderId),
            }))
            setOrders(updatedOrders)
          }
        }
      },
    )
  }

  const handleOrderIdChange = (index: number, value: string) => {
    const updatedOrders = [...orders]
    updatedOrders[index] = {
      ...updatedOrders[index],
      orderId: value,
      isInvalid: false,
    }
    setOrders(updatedOrders)

    // Debounce and validate with updated list
    if (validateTimeoutRef.current) {
      clearTimeout(validateTimeoutRef.current)
    }
    validateTimeoutRef.current = setTimeout(() => {
      validateOrderIds(updatedOrders)
    }, 500)
  }

  const handleStatusChange = (index: number, value: string) => {
    const updatedOrders = [...orders]
    updatedOrders[index] = {
      ...updatedOrders[index],
      status: value,
    }
    setOrders(updatedOrders)
  }

  const triggerFileUpload = () => {
    fileInputRef.current?.click()
  }

  const downloadTemplate = () => {
    const template = [{ orderId: '', status: '' }]
    const ws = XLSX.utils.json_to_sheet(template)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Template')
    XLSX.writeFile(wb, 'order_status_template.xlsx')
  }

  return (
    <DcFlexBox
      flexDirection="column"
      style={{ maxHeight: '80vh', overflow: 'hidden' }}
    >
      <DcFlexBox height="auto" gap="5px" style={{ alignItems: 'center' }}>
        {' '}
        <span> Easily update status of your orders with our</span>
        <DcButton
          style={{
            width: 'auto',
            height: 'auto',
            padding: '5px !important',
            display: 'inline-block',
          }}
          padding="0px !important"
          variant="link"
          onClick={downloadTemplate}
        >
          template
        </DcButton>
      </DcFlexBox>
      {orders.length === 0 ? (
        <DcFlexBox
          flexDirection="column"
          style={{ alignItems: 'center' }}
          gap="16px"
          padding="32px"
          border={`2px dashed ${colors.grayBorderLight}`}
          borderRadius="8px"
          cursor="pointer"
          transition="all 0.2s ease"
          hoverStyle={{
            borderColor: colors.grayTextMedium,
            backgroundColor: colors.grayLighter,
          }}
        >
          <DcButton onClick={triggerFileUpload}>
            <Upload size={16} /> &nbsp; Upload File
          </DcButton>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx, .xls"
            data-testid="file-input"
            style={{ display: 'none' }}
          />
        </DcFlexBox>
      ) : (
        <SectionContent
          style={{ flexDirection: 'column', gap: '16px', display: 'flex' }}
        >
          {errorMessage && (
            <DcFlexBox
              height="auto"
              style={{
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                borderRadius: '4px',
                backgroundColor: colors.dangerLight,
                color: colors.danger,
                fontSize: '14px',
              }}
            >
              <AlertCircle size={20} />
              <span>{errorMessage}</span>
            </DcFlexBox>
          )}

          {successMessage && (
            <DcFlexBox
              height="auto"
              style={{
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                borderRadius: '4px',
                backgroundColor: colors.successLight,
                color: colors.success,
                fontSize: '14px',
              }}
            >
              <CheckCircle2 size={20} />
              <span>{successMessage}</span>
            </DcFlexBox>
          )}

          <DcDiv
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap: '12px',
              overflowY: 'auto',
              maxHeight: '60vh',
              height: 'auto',
              padding: '8px',
            }}
          >
            {orders.map((order, index) => (
              <DcFlexBox height="auto" key={index} gap="8px">
                <DcInput
                  value={order.orderId}
                  onChange={(e) => handleOrderIdChange(index, e.target.value)}
                  isShowError={!!order.isInvalid}
                  errorMessage={order.isInvalid ? 'Invalid order ID' : ''}
                  style={{
                    padding: '8px 12px',
                    border: `1px solid ${colors.grayBorderLight}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '100%',
                    backgroundColor: order.isInvalid
                      ? colors.dangerLight
                      : colors.white,
                    borderColor: colors.grayMedium,
                    '&:focus': {
                      borderColor: colors.grayBorderLight,
                      outline: 'none',
                    },
                  }}
                  placeholder="Order ID"
                />
                <DcInput
                  value={order.status}
                  onChange={(e) => handleStatusChange(index, e.target.value)}
                  placeholder="Status"
                />
              </DcFlexBox>
            ))}
          </DcDiv>
          <DcFlexBox height="auto" justifyContent="flex-end" gap="5px">
            <DcButton
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                handelClose()
              }}
            >
              Close
            </DcButton>
            <DcButton
              size="sm"
              onClick={handleUpdateStatuses}
              disabled={!!invalidOrderIds.length}
            >
              {isLoading ? 'Updating...' : <>&nbsp; Update</>}
            </DcButton>
          </DcFlexBox>
        </SectionContent>
      )}
    </DcFlexBox>
  )
}
